import { Category, Gender, TeamComposition, TeamType, UserRole } from '../types/index.js';
// Format date to string using specified format or default format
export const formatDate = (date, format = 'MM/DD/YYYY') => {
    const dateObj = typeof date === 'object' ? date : new Date(date);
    // Simple formatting - in a real application, we'd use a library like date-fns
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    // Replace format tokens with actual values
    return format.replace('MM', month).replace('DD', day).replace('YYYY', year.toString());
};
// Get the current age from birth year
export const getAgeFromBirthYear = (birthYear) => {
    const currentYear = new Date().getFullYear();
    return currentYear - birthYear;
};
// Validate team composition based on team type
export const validateTeamComposition = (teamType, players) => {
    if (teamType === TeamType.SINGLES) {
        if (players.length !== 1) {
            return {
                valid: false,
                message: 'Singles team must have exactly one player.',
            };
        }
    }
    else if (teamType === TeamType.DOUBLES) {
        if (players.length !== 2) {
            return {
                valid: false,
                message: 'Doubles team must have exactly two players.',
            };
        }
    }
    else if (teamType === TeamType.MIXED_DOUBLES) {
        if (players.length !== 2) {
            return {
                valid: false,
                message: 'Mixed doubles team must have exactly two players.',
            };
        }
        const hasMale = players.some(player => player.gender === Gender.MALE);
        const hasFemale = players.some(player => player.gender === Gender.FEMALE);
        if (!hasMale || !hasFemale) {
            return {
                valid: false,
                message: 'Mixed doubles team must have one male and one female player.',
            };
        }
    }
    return { valid: true };
};
// Check if user has required role
export const hasRole = (userRoles, requiredRole) => {
    if (!userRoles)
        return false;
    const roleHierarchy = {
        [UserRole.PLAYER]: 1,
        [UserRole.TEAM_CAPTAIN]: 2,
        [UserRole.LEAGUE_ADMINISTRATOR]: 3,
        [UserRole.SUPER_ADMINISTRATOR]: 4,
    };
    const requiredRoleLevel = roleHierarchy[requiredRole];
    return userRoles.some(role => roleHierarchy[role] >= requiredRoleLevel);
};
// Calculate points based on game result
export const calculatePoints = (winnerScore, loserScore, basePoints = 100) => {
    if (winnerScore <= loserScore) {
        throw new Error('Winner score must be greater than loser score');
    }
    const scoreDifference = winnerScore - loserScore;
    const dominanceFactor = scoreDifference / (winnerScore + loserScore);
    // Bonus points for dominating performance (max 50% bonus)
    const bonusPoints = Math.floor(basePoints * Math.min(dominanceFactor, 0.5));
    return basePoints + bonusPoints;
};
// Generate a random string for token generation, etc.
export const generateRandomString = (length = 32) => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
};
// Create pagination metadata
export const createPaginationMeta = (page, limit, total) => {
    const pages = Math.ceil(total / limit);
    return {
        page,
        limit,
        total,
        pages,
    };
};
// Truncate a string to specified length
export const truncateString = (str, maxLength = 100) => {
    if (str.length <= maxLength)
        return str;
    return `${str.substring(0, maxLength - 3)}...`;
};
// Parse query parameters from URL
export const parseQueryParams = (queryString) => {
    if (!queryString || !queryString.trim().startsWith('?'))
        return {};
    const params = new URLSearchParams(queryString);
    const result = {};
    params.forEach((value, key) => {
        // Try to parse numbers and booleans
        if (!isNaN(Number(value))) {
            result[key] = Number(value);
        }
        else if (value === 'true' || value === 'false') {
            result[key] = value === 'true';
        }
        else {
            result[key] = value;
        }
    });
    return result;
};
// Deep clone an object
export const deepClone = (obj) => {
    return JSON.parse(JSON.stringify(obj));
};
// Convert a positive integer to a Roman numeral (1 → "I", 2 → "II", 3 → "III", …).
// Used to name division tiers as `${baseName} ${toRoman(level)}`. Falls back to the
// decimal string for non-positive / non-integer input.
export const toRoman = (n) => {
    if (!Number.isInteger(n) || n <= 0)
        return String(n);
    const map = [
        [1000, 'M'],
        [900, 'CM'],
        [500, 'D'],
        [400, 'CD'],
        [100, 'C'],
        [90, 'XC'],
        [50, 'L'],
        [40, 'XL'],
        [10, 'X'],
        [9, 'IX'],
        [5, 'V'],
        [4, 'IV'],
        [1, 'I'],
    ];
    let result = '';
    let rem = n;
    for (const [value, sym] of map) {
        while (rem >= value) {
            result += sym;
            rem -= value;
        }
    }
    return result;
};
// Derive a team's badminton composition from its format and its players' genders.
// (Gender 'other' is treated as male for composition purposes.)
export const deriveTeamComposition = (type, genders) => {
    if (type === TeamType.SINGLES) {
        return genders[0] === Gender.FEMALE
            ? TeamComposition.WOMENS_SINGLES
            : TeamComposition.MENS_SINGLES;
    }
    const females = genders.filter(g => g === Gender.FEMALE).length;
    if (females >= 2)
        return TeamComposition.WOMENS_DOUBLES;
    if (females === 1)
        return TeamComposition.MIXED;
    return TeamComposition.MENS_DOUBLES;
};
// The composition a category corresponds to by default (used when a division does
// not explicitly configure its accepted compositions).
export const defaultCompositionForCategory = (category) => {
    switch (category) {
        case Category.WOMENS_SINGLES:
            return TeamComposition.WOMENS_SINGLES;
        case Category.MENS_SINGLES:
            return TeamComposition.MENS_SINGLES;
        case Category.WOMENS_DOUBLES:
            return TeamComposition.WOMENS_DOUBLES;
        case Category.MIXED_DOUBLES:
            return TeamComposition.MIXED;
        case Category.MENS_DOUBLES:
        default:
            return TeamComposition.MENS_DOUBLES;
    }
};
// Whether a team composition is accepted by a division. An empty/undefined
// eligible list falls back to the category's own default composition.
//
// NOTE: This is retained for display/legacy use only. Enrollment eligibility is
// now governed by team size + the division's explicit gender restriction — see
// `isTeamEligibleForDivision` below.
export const isCompositionEligible = (composition, eligibleCompositions, category) => {
    const allowed = eligibleCompositions && eligibleCompositions.length > 0
        ? eligibleCompositions
        : [defaultCompositionForCategory(category)];
    return allowed.includes(composition);
};
// ─── Division enrollment eligibility (size + explicit gender restriction) ──────
//
// A division's category implies a team SIZE (singles = 1 player, doubles/mixed =
// 2 players) and is otherwise just a display name. Whether a team may enroll is
// gated by the division's explicit gender restriction — categories do NOT
// auto-gate by gender. Mixed Doubles is the sole exception: it always forces
// exactly one male + one female. ('other' is treated as non-female, consistent
// with `deriveTeamComposition`.)
/** The number of players a division of this category requires. */
export const teamSizeForCategory = (category) => category === Category.MENS_SINGLES || category === Category.WOMENS_SINGLES ? 1 : 2;
/**
 * The effective gender rule for a division. Mixed Doubles always forces a
 * male+female pair; otherwise the admin's explicit `genderRestriction` applies
 * ('none'/empty/undefined → no restriction).
 */
export const genderRuleForDivision = (category, genderRestriction) => {
    if (category === Category.MIXED_DOUBLES)
        return 'mixed';
    switch (genderRestriction) {
        case 'male':
            return 'male';
        case 'female':
            return 'female';
        case 'mixed':
            return 'mixed';
        default:
            return 'any';
    }
};
/**
 * Whether a team (given its players' genders) may enroll into a division. The
 * team size must match the category, and the genders must satisfy the division's
 * gender rule.
 */
export const isTeamEligibleForDivision = (genders, category, genderRestriction) => {
    if (genders.length !== teamSizeForCategory(category))
        return false;
    const females = genders.filter(g => g === Gender.FEMALE).length;
    const nonFemales = genders.length - females;
    switch (genderRuleForDivision(category, genderRestriction)) {
        case 'male':
            return females === 0;
        case 'female':
            return nonFemales === 0;
        case 'mixed':
            return genders.length === 2 && females === 1 && nonFemales === 1;
        case 'any':
        default:
            return true;
    }
};
